import moment from 'moment';

import { readArchive } from '../../../util/archive';

import { DATE_FORMAT_OUTPUT, MESSAGES_BLOCK_COUNT } from '../ConversationActions';
import { stripTags } from '../../../util/text';

// Export officiel LinkedIn (« Download your data ») : archive .zip contenant messages.csv
// à la racine, et Profile.csv quand l'archive complète est demandée.
// Les pièces jointes ne sont que des URLs privées linkedin.com/dms/ qui exigent une
// session authentifiée : aucun média n'est récupérable, l'import est donc texte seul.

const MESSAGES_FILE_REGEX = /(^|\/)messages\.csv$/i;
const PROFILE_FILE_REGEX = /(^|\/)profile\.csv$/i;

const COLUMN_CONVERSATION_ID = 'CONVERSATION ID';
const COLUMN_CONVERSATION_TITLE = 'CONVERSATION TITLE';
const COLUMN_FROM = 'FROM';
const COLUMN_TO = 'TO';
const COLUMN_DATE = 'DATE';
const COLUMN_CONTENT = 'CONTENT';
const COLUMN_DRAFT = 'IS MESSAGE DRAFT';
const COLUMN_FIRST_NAME = 'FIRST NAME';
const COLUMN_LAST_NAME = 'LAST NAME';

// Publicités LinkedIn (InMail sponsorisés) livrées dans la messagerie : jamais imprimées.
const SPONSORED_TITLES = ['sponsored conversation'];
// Expéditeur masqué par LinkedIn (compte supprimé ou annonceur) : inutilisable comme nom.
const ANONYMOUS_SENDER = 'linkedin member';

const HTML_ENTITIES = {
    amp: '&',
    lt: '<',
    gt: '>',
    quot: '"',
    apos: '\'',
    nbsp: ' ',
};

const DATE_FORMATS = ['YYYY-MM-DD HH:mm:ss', 'YYYY-MM-DD HH:mm', 'YYYY/MM/DD HH:mm:ss', moment.ISO_8601];

// Read selected file by user
// For LinkedIn: user drops the whole archive, or messages.csv alone
export function readFile(file, options, onProgress) {
    if(isCsvFile(file)) {
        return new Promise(resolve => {
            const reader = new FileReader();
            reader.onload = () => {
                onProgress(100);
                resolve([
                    {
                        path: file.name,
                        filename: file.name,
                        content: reader.result,
                    },
                ]);
            };
            reader.readAsText(file);
        });
    }
    return readArchive(file, options, onProgress);
}

export function getFiles() {
    return Promise.resolve([]);
}

export function canHandleImages() {
    return false;
}

// Retrieve profile name
export function getProfileName(entries) {
    const profileEntry = (entries || []).find(entry => isTextEntry(entry) && PROFILE_FILE_REGEX.test(getEntryPath(entry)));

    if(profileEntry) {
        const [profile] = parseCsv(profileEntry.content);
        const profileName = `${(profile || {})[COLUMN_FIRST_NAME] || ''} ${(profile || {})[COLUMN_LAST_NAME] || ''}`.trim();
        if(profileName) {
            return profileName;
        }
    }

    return getMostPresentParticipant(getMessagesRows(entries));
}

// Retrieve threads names
// Un seul messages.csv contient toutes les conversations : le regroupement se fait
// sur CONVERSATION ID, pas sur les fichiers comme chez Meta.
export function getThreads(entries) {
    const rows = getMessagesRows(entries);
    const profileName = getProfileName(entries);

    const threads = [];
    const threadsById = {};

    rows.forEach(row => {
        if(!isPrintableRow(row)) {
            return;
        }

        const id = getThreadId(row, profileName);
        if(!threadsById[id]) {
            threadsById[id] = {
                id,
                providerId: id,
                title: '',
                fallbackName: '',
                names: {},
                count: 0,
            };
            threads.push(threadsById[id]);
        }

        const thread = threadsById[id];
        thread.count += 1;
        thread.title = thread.title || getRowTitle(row);

        getRowParticipants(row).forEach(name => {
            if(name === profileName) {
                return;
            }
            if(name.toLowerCase() === ANONYMOUS_SENDER) {
                // Contact anonymisé par LinkedIn : mieux vaut ce libellé qu'un identifiant technique.
                thread.fallbackName = thread.fallbackName || name;
            } else {
                thread.names[name] = (thread.names[name] || 0) + 1;
            }
        });
    });

    return threads.map(({ names, title, fallbackName, ...thread }) => {
        const contactName = Object.keys(names).sort((nameA, nameB) => names[nameB] - names[nameA]).shift();
        return {
            ...thread,
            name: contactName || title || fallbackName || thread.id,
        };
    });
}

// Generate CSV Content
// Separate messages in distinct csv (10 000 messages / csv)
export function getArchiveContent(entries, from, thread, options = { withImages: false, withMedias: false }, onProgress = percentile => {}) {
    const participants = [];
    const messagesBlocks = [];

    onProgress(1); // Init progress

    const profileName = from || getProfileName(entries);
    const threadId = `${(thread || {}).providerId || (thread || {}).id || ''}`.trim();
    const hasThreadFilter = threadId && threadId !== 'default';

    const messages = getMessagesRows(entries)
        .filter(row => isPrintableRow(row))
        .filter(row => !hasThreadFilter || getThreadId(row, profileName) === threadId)
        .map(row => getMessageContent(row, profileName))
        .filter(message => message && message.messageCsv)
        .sort((messageA, messageB) => messageA.timestamp - messageB.timestamp);

    const iteration = Math.floor(messages.length / MESSAGES_BLOCK_COUNT);
    const percentile = 100 / (iteration + 1);

    for(let i = 0; i <= iteration; i += 1) {
        const messagesBlock = messages.slice(i * MESSAGES_BLOCK_COUNT, (i * MESSAGES_BLOCK_COUNT) + MESSAGES_BLOCK_COUNT);

        onProgress(percentile);

        if(messagesBlock.length) {
            let csv = '';
            messagesBlock.forEach(message => {
                if(!participants.includes(message.messageParticipant)) {
                    participants.push(message.messageParticipant);
                }
                csv += `${message.messageCsv}\n`;
            });

            messagesBlocks.push({
                sort: i,
                filename: `messages_${i + 1}.csv`,
                content: csv,
                messages: messagesBlock,
                start: new Date(messagesBlock[0].timestamp),
                end: new Date(messagesBlock[messagesBlock.length - 1].timestamp),
            });
        } else if(messages.length) {
            console.error('Missing messages in block', i, participants);
        }
    }

    return {
        participants,
        messagesBlocks,
        images: [],
        medias: [],
    };
}

function isCsvFile(file) {
    return `${(file || {}).type || ''}`.includes('csv') || /\.csv$/i.test(`${(file || {}).name || ''}`);
}

function isTextEntry(entry) {
    return !!entry && typeof entry.content === 'string' && !!entry.content;
}

function getEntryPath(entry) {
    return `${(entry || {}).path || (entry || {}).filename || ''}`;
}

// L'utilisateur peut déposer l'archive complète ou le seul messages.csv, parfois renommé :
// on privilégie le fichier attendu, sinon on retombe sur les autres CSV de l'archive.
function getMessagesEntries(entries) {
    const textEntries = (entries || []).filter(entry => isTextEntry(entry) && !PROFILE_FILE_REGEX.test(getEntryPath(entry)));
    const messagesEntries = textEntries.filter(entry => MESSAGES_FILE_REGEX.test(getEntryPath(entry)));
    return messagesEntries.length ? messagesEntries : textEntries;
}

function getMessagesRows(entries) {
    return getMessagesEntries(entries)
        .reduce((rows, entry) => rows.concat(parseCsv(entry.content)), [])
        .filter(row => row[COLUMN_CONTENT] !== undefined && row[COLUMN_DATE] !== undefined);
}

// Parseur CSV conforme RFC 4180 : LinkedIn place des guillemets et des retours ligne
// dans la colonne CONTENT, un simple split(',') découperait les messages au milieu.
function parseCsvRows(content) {
    const text = `${content || ''}`.replace(/^\uFEFF/, '');
    const rows = [];
    let row = [];
    let value = '';
    let isQuoted = false;

    for(let i = 0; i < text.length; i += 1) {
        const char = text[i];

        if(isQuoted) {
            if(char !== '"') {
                value += char;
            } else if(text[i + 1] === '"') {
                value += '"';
                i += 1;
            } else {
                isQuoted = false;
            }
        } else if(char === '"') {
            isQuoted = true;
        } else if(char === ',') {
            row.push(value);
            value = '';
        } else if(char === '\n' || char === '\r') {
            if(char === '\r' && text[i + 1] === '\n') {
                i += 1;
            }
            row.push(value);
            rows.push(row);
            row = [];
            value = '';
        } else {
            value += char;
        }
    }

    if(value || row.length) {
        row.push(value);
        rows.push(row);
    }

    return rows;
}

function parseCsv(content) {
    const rows = parseCsvRows(content);
    const header = (rows.shift() || []).map(column => `${column}`.trim().toUpperCase());

    if(!header.length) {
        return [];
    }

    return rows
        .filter(row => row.some(value => `${value}`.trim()))
        .map(row => {
            const line = {};
            header.forEach((column, index) => {
                line[column] = row[index] === undefined ? '' : row[index];
            });
            return line;
        });
}

function getRowTitle(row) {
    return `${row[COLUMN_CONVERSATION_TITLE] || ''}`.trim();
}

function getRowParticipants(row) {
    return [`${row[COLUMN_FROM] || ''}`]
        .concat(`${row[COLUMN_TO] || ''}`.split(','))
        .map(name => name.trim())
        .filter(name => name);
}

function getThreadId(row, profileName) {
    const conversationId = `${row[COLUMN_CONVERSATION_ID] || ''}`.trim();
    if(conversationId) {
        return conversationId;
    }
    // Anciens exports sans CONVERSATION ID : le contact fait office de fil.
    return getRowParticipants(row).find(name => name !== profileName) || 'conversation';
}

function isSponsoredRow(row) {
    return SPONSORED_TITLES.includes(getRowTitle(row).toLowerCase());
}

function isDraftRow(row) {
    return ['yes', 'true', '1'].includes(`${row[COLUMN_DRAFT] || ''}`.trim().toLowerCase());
}

function isPrintableRow(row) {
    return !isSponsoredRow(row)
        && !isDraftRow(row)
        && !!getMessageText(row)
        && !!getMessageDate(row);
}

function getMessageContent(row, from) {
    const sender = `${row[COLUMN_FROM] || ''}`.trim();
    const recipients = `${row[COLUMN_TO] || ''}`.split(',').map(name => name.trim()).filter(name => name);
    const isSender = sender === from;
    const date = getMessageDate(row);

    // Le CSV interne est séparé par « ; » : un nom en contenant décalerait toutes les colonnes.
    const data = {
        date: date.local().format(DATE_FORMAT_OUTPUT).split('+').shift(),
        sender: sender.replace(/;/g, ','),
        to: (isSender ? (recipients.join(', ') || from) : from).replace(/;/g, ','),
        content: encodeURIComponent(getMessageText(row)),
        isSender: isSender ? 'true' : 'false',
        medias: '',
    };

    return {
        messageParticipant: sender,
        messageCsv: Object.keys(data).map(key => (data[key] || '')).join(';'),
        date: data.date,
        timestamp: date.valueOf(),
    };
}

function getMessageDate(row) {
    const value = `${row[COLUMN_DATE] || ''}`.trim().replace(/\s*UTC\s*$/i, '');
    if(!value) {
        return null;
    }
    const date = moment.utc(value, DATE_FORMATS);
    return date.isValid() ? date : null;
}

function getMessageText(row) {
    const content = `${row[COLUMN_CONTENT] || ''}`;

    if(!content.trim()) {
        return '';
    }

    // Les InMail et messages sponsorisés arrivent en HTML : on repasse en texte brut
    // en conservant les sauts de paragraphe. Les messages normaux sont laissés tels quels
    // pour ne pas amputer un texte contenant « < » ou « > ».
    const text = /<[a-z!/][^>]*>/i.test(content)
        ? stripTags(content.replace(/<\s*br\s*\/?\s*>/gi, '\n').replace(/<\s*\/\s*(p|div|li|tr|h[1-6])\s*>/gi, '\n'))
        : content;

    return decodeHtmlEntities(text)
        .replace(/\r\n?/g, '\n')
        .replace(/\n{3,}/g, '\n\n')
        .trim();
}

function decodeHtmlEntities(text) {
    return `${text || ''}`
        .replace(/&#(\d+);/g, (match, code) => String.fromCharCode(parseInt(code, 10)))
        .replace(/&#x([0-9a-f]+);/gi, (match, code) => String.fromCharCode(parseInt(code, 16)))
        .replace(/&([a-z]+);/gi, (match, entity) => {
            const decoded = HTML_ENTITIES[entity.toLowerCase()];
            return decoded === undefined ? match : decoded;
        })
        .replace(/\u00a0/g, ' ');
}

// Le titulaire de l'export est le seul présent dans (presque) toutes les conversations :
// plus fiable que « l'expéditeur le plus fréquent » quand la boîte est surtout entrante.
function getMostPresentParticipant(rows) {
    const threadsByName = {};

    rows.forEach(row => {
        const conversationId = `${row[COLUMN_CONVERSATION_ID] || ''}`.trim();
        getRowParticipants(row).forEach(name => {
            if(name.toLowerCase() !== ANONYMOUS_SENDER) {
                threadsByName[name] = threadsByName[name] || {};
                threadsByName[name][conversationId] = true;
            }
        });
    });

    return Object.keys(threadsByName)
        .sort((nameA, nameB) => Object.keys(threadsByName[nameB]).length - Object.keys(threadsByName[nameA]).length)
        .shift() || '';
}
